import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import RootFractionInput, {
  parseRootFractionInput,
} from "../../../../components/inputs/RootFractionInput";
import { formatLatexPreview } from "../../../../components/inputs/RootFractionInput";

// (wstaw obok pozostałych utili na górze pliku)
function simplifySqrtForDisplay(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside }; // r = outside * sqrt(inside)
}

// BEZPIECZNY gcd (iteracyjny, na intach) + guard na NaN/Infinity
function gcdInt(a, b) {
  a = Math.abs(Math.trunc(a));
  b = Math.abs(Math.trunc(b));

  if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
  if (a === 0 && b === 0) return 1;

  while (b !== 0) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a === 0 ? 1 : a;
}

// uproszczone z usunięciem niewymierności z mianownika
function simplifySqrtFraction(numerator, radicand) {
  let outside = 1;
  let inside = radicand;

  for (let i = 2; i * i <= radicand; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }

  const num = numerator * outside; // licznik po wyciągnięciu kwadratów z pierwiastka
  const den = radicand; // jeszcze przed skróceniem

  const d = gcdInt(num, den);

  return {
    numerator: num / d, // współczynnik przed sqrt(inside)
    root: inside, // to co zostaje pod pierwiastkiem
    denominator: den / d, // mianownik po skróceniu
  };
}

function randomChoice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function generateTask() {
  const func = "sin";

  const xCandidates = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];
  const yCandidates = [1, 2, 3, 4, 5, 6, 7, 8];

  const x = randomChoice(xCandidates);
  const y = randomChoice(yCandidates);
  const radicand = x ** 2 + y ** 2;

  const expected = simplifySqrtFraction(y, radicand);

  // ✅ KLUCZOWE: dodajemy pole data, bo QuizRunner woła renderExplanation({data: task.data})
  const data = {
    type: "FindSinFromPointTask",
    func,
    x,
    y,
    expected,
    radicand,
  };

  return {
    ...data,
    data, // <- to ratuje wyjaśnienia w quizie
  };
}

export function renderTask(task) {
  const { x, y } = task;

  return (
    <div className="space-y-4" style={{ marginTop: "15px" }}>
      <div>
        Dany jest punkt <InlineMath math={`P = (${x}, ${y})`} />. Oblicz{" "}
        <InlineMath math={`\\sin\\, \\alpha`} />, gdzie{" "}
        <InlineMath math={`\\alpha`} /> to kąt pomiędzy odcinkiem{" "}
        <InlineMath math={`OP`} />, a dodatnią osią <InlineMath math={`x`} />.
      </div>
      <div style={{ height: 360 }}>
        <GraphViewer
          points={{
            O: {
              x: 0,
              y: 0,
              textPosition: x > 0 && y > 0 ? "top left" : "top right",
            },
            P: { x, y },
          }}
          segments={[{ p1: "O", p2: "P" }]}
          xRange={[-6, 6]}
          yRange={[-3, 9]}
          width={350}
          height={350}
        />
      </div>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\sin\\,\\alpha`}
    />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <InlineMath
      math={`\\displaystyle ${formatLatexPreview(raw, "\\sin\\,\\alpha")}`}
    />
  );
}

export function checkAnswer(task, userAnswer) {
  const { y, radicand, expected } = task;
  const expectedValue = y / Math.sqrt(radicand);

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if (parsed != null && isFinite(parsed)) {
    if (Math.abs(parsed - expectedValue) < 0.01) return true;
  }

  const { numerator, root, denominator } = expected;

  let numStr;
  if (numerator === 1) numStr = "";
  else if (numerator === -1) numStr = "-";
  else numStr = `${numerator}`;

  const rootPart = root === 1 ? "" : `\\sqrt{${root}}`;
  const top = `${numStr}${rootPart}` || "1";
  const expectedLatex =
    denominator === 1 ? top : `\\dfrac{${top}}{${denominator}}`;

  const normalize = (str) =>
    String(str ?? "")
      .replace(/\s/g, "")
      .replace(/\*/g, "")
      .replace(/sqrt\(([^)]+)\)/g, "√$1")
      .replace(/\\sqrt{([^}]+)}/g, "√$1")
      .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/[()]/g, "")
      .replace(/\\/g, "");

  return normalize(raw) === normalize(expectedLatex);
}

export function renderAnswer({ expected }) {
  const { numerator, root, denominator } = expected;

  const sign = numerator < 0 ? "-" : "";
  const absNum = Math.abs(numerator);
  const numStr = absNum === 1 ? "" : `${absNum}`;
  const rootPart = root === 1 ? "" : `\\sqrt{${root}}`;
  const top = `${numStr}${rootPart}` || "1";

  return (
    <InlineMath
      math={`\\sin\\, \\alpha = ${
        denominator === 1
          ? `${sign}${top}`
          : `${sign}\\dfrac{${top}}{${denominator}}`
      }`}
    />
  );
}

export function renderExplanation(payload) {
  // ✅ działa i dla starego wywołania renderExplanation(task)
  // ✅ i dla quizowego renderExplanation({ data: task.data, answer: ... })
  const t =
    payload && payload.data && payload.data.x != null ? payload.data : payload;

  const x = t?.x;
  const y = t?.y;

  const rad = t?.radicand ?? (x != null && y != null ? x * x + y * y : null);
  if (x == null || y == null || rad == null || !Number.isFinite(rad)) {
    // awaryjnie (żeby NIGDY nie wysypać całej strony)
    return <div>(brak danych do wyjaśnienia)</div>;
  }

  const expectedSafe = t?.expected ?? simplifySqrtFraction(y, rad);
  const { numerator, root, denominator } = expectedSafe;

  const sign = numerator < 0 ? "-" : "";
  const absNum = Math.abs(numerator);
  const numStr = absNum === 1 ? "" : `${absNum}`;
  const rootPart = root === 1 ? "" : `\\sqrt{${root}}`;
  const top = `${numStr}${rootPart}` || "1";

  const xStr = x < 0 ? `(${x})` : `${x}`;
  const yStr = y < 0 ? `(${y})` : `${y}`;

  const { outside, inside } = simplifySqrtForDisplay(rad);
  const simplifiedR =
    inside === 1
      ? `${outside}`
      : outside === 1
        ? `\\sqrt{${inside}}`
        : `${outside}\\sqrt{${inside}}`;

  const finalLatex =
    denominator === 1
      ? `${sign}${top}`
      : `${sign}\\dfrac{${top}}{${denominator}}`;
  const firstSub = `\\dfrac{${y}}{${simplifiedR}}`;
  const showEq = firstSub !== finalLatex;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Obliczamy długość odcinka{" "}
        <InlineMath math="r = |OP|" />:
        <BlockMath math={`r = \\sqrt{${xStr}^2 + ${yStr}^2} = \\sqrt{${rad}}`} />
        {(outside > 1 || inside === 1) && <BlockMath math={`r = ${simplifiedR}`} />}
      </div>
      <div>
        <strong>Krok 2:</strong> Korzystamy ze wzoru:
        <BlockMath math={`\\sin\\, \\alpha = \\dfrac{y}{r}`} />
        Podstawiamy:
        <BlockMath
          math={`\\sin\\, \\alpha = ${
            showEq ? `${firstSub} = ${finalLatex}` : firstSub
          }`}
        />
      </div>
    </div>
  );
}
